import clsx from 'clsx';

import { ButtonLink } from '@commons/components/Button';
import RichText from '@commons/components/RichText';
import Texture from '@commons/components/Texture';
import Flex from '@commons/containers/Flex';
import PageBuilder from '@commons/pagebuilder';
import WebformDrawer from '@commons/pagebuilder/webforms/Drawer';
import type { DrupalBlock, DrupalLink } from '@commons/types/drupal';
import type { Theme } from '@commons/types/styles';

import styles from './index.module.scss';

// Type definition for the TexturedTileBlock component properties.

type Props = {
  body: string;
  theme: Theme;
  direction: 'row' | 'column';
  buttonType?: 'default' | 'webform';
  link?: DrupalLink;
  webform?: { id: string };
  webformButtonLabel?: string;
  animated?: boolean;
  aspectRatio?: 'square' | 'portrait';
  borderRadius?: 'round' | 'circle';
};

/**
 * TexturedTileBlock component.
 */

const TexturedTileBlock = ({ content }: DrupalBlock<Props>) => {
  const {
    body,
    theme,
    buttonType,
    direction,
    animated = false,
    borderRadius,
    aspectRatio,
  } = content;

  const isCircle = borderRadius === 'circle';
  const isDirectionColumn = direction === 'column';

  const classes = clsx(
    styles.wrapper,
    aspectRatio && styles[aspectRatio],
    borderRadius && styles[borderRadius],
    !aspectRatio && 'w-100 h-100'
  );

  return (
    <Flex
      gap="sm"
      width={12}
      theme={theme}
      align={isCircle ? 'center' : { xs: 'start', lg: isDirectionColumn ? 'start' : 'center' }}
      justify={isCircle ? 'center' : 'space-between'}
      padding={isDirectionColumn ? 'sm' : { xs: 'sm', lg: 'xl' }}
      direction={{ xs: 'column', lg: direction }}
      className={classes}
    >
      <Texture animated={animated} />
      <RichText
        value={body}
        width={isDirectionColumn ? 12 : { xs: 12, lg: 6 }}
        className={styles.content}
      />
      {buttonType && <TexturedTileButton {...content} />}
    </Flex>
  );
};

/**
 * TexturedTileBlock button component.
 *
 * Makes it easier to conditionally render a button based on the buttonType prop.
 */

const TexturedTileButton = ({
  direction,
  buttonType,
  link,
  webform,
  webformButtonLabel,
}: Props) => {
  const isButtonXl = direction === 'row';

  if (buttonType === 'default' && link?.url && link?.text) {
    return (
      <ButtonLink href={link.url} variant="secondary" xl={isButtonXl}>
        {link.text}
      </ButtonLink>
    );
  }

  if (buttonType === 'webform' && webform?.id && webformButtonLabel) {
    const renderedWebform = PageBuilder.renderWebform(webform.id, { inDrawer: true });
    return (
      <WebformDrawer
        webformId={webform.id}
        triggerProps={{ label: webformButtonLabel, xl: isButtonXl }}
      >
        {renderedWebform}
      </WebformDrawer>
    );
  }

  return null;
};

export default TexturedTileBlock;
